import { h, FunctionalComponent } from "@stencil/core";
import { getViewOfMonth, inRange, DaysOfWeek, isEqual } from "../../utils/date-utils";

import { StzhCalendarLocalizedText } from "./stzh-calendar.localization";
import { StzhCalendarDay, StzhCalendarDayProps } from "./stzh-calendar-day";
import { StzhCalendarDateDisabledPredicate } from "../../index";

function chunk<T>(array: T[], chunkSize: number): T[][] {
  const result = [];

  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }

  return result;
}

function mapWithOffset<T, U>(array: T[], startingOffset: number, mapFn: (item: T) => U): U[] {
  return array.map((_, i) => {
    const adjustedIndex = (i + startingOffset) % array.length;
    return mapFn(array[adjustedIndex]);
  });
}

type StzhCalendarMonthProps = {
  selectedDate: Date;
  focusedDate: Date;
  labelledById: string;
  localization: StzhCalendarLocalizedText;
  firstDayOfWeek: DaysOfWeek;
  min?: Date;
  max?: Date;
  dateFormatter: Intl.DateTimeFormat;
  isDateDisabled: StzhCalendarDateDisabledPredicate;
  onDateSelect: StzhCalendarDayProps["onDaySelect"];
  onKeyboardNavigation: StzhCalendarDayProps["onKeyboardNavigation"];
  focusedDayRef: (element: HTMLElement) => void;
};

export const StzhCalendarMonth: FunctionalComponent<StzhCalendarMonthProps> = ({
  selectedDate,
  focusedDate,
  labelledById,
  localization,
  firstDayOfWeek,
  min,
  max,
  dateFormatter,
  isDateDisabled,
  onDateSelect,
  onKeyboardNavigation,
  focusedDayRef,
}) => {
  const today = new Date();
  const days = getViewOfMonth(focusedDate, firstDayOfWeek);

  return (
    <table class="stzh-calendar__table" aria-labelledby={labelledById}>
      <thead>
        <tr>
          {mapWithOffset(localization.$globals.dayNames, firstDayOfWeek, dayName => (
            <th class="stzh-calendar__table-header" scope="col">
              <span aria-hidden="true">{dayName.substring(0, 2)}</span>
              <span class="stzh-calendar__vhidden">{dayName}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {chunk(days, 7).map(week => (
          <tr class="stzh-calendar__row">
            {week.map(day => (
              <td class="stzh-calendar__cell">
                <StzhCalendarDay
                  day={day}
                  today={today}
                  focusedDay={focusedDate}
                  isSelected={isEqual(day, selectedDate)}
                  disabled={isDateDisabled(day)}
                  inRange={inRange(day, min, max)}
                  onDaySelect={onDateSelect}
                  dateFormatter={dateFormatter}
                  onKeyboardNavigation={onKeyboardNavigation}
                  focusedDayRef={focusedDayRef}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
